import { QueryCache, QueryClient } from '@tanstack/react-query'
import type { AuthMeResponse } from '@acs/shared'
import { NO_FEATURES } from '@acs/shared'
import { ApiError } from './api'

/**
 * Cache policy for a console whose truth arrives over SSE.
 *
 * Why `staleTime` is deliberately high (30s):
 * this app is *push*-driven. `useRealtime()` merges `customer.patch`,
 * `session.patch`, `log.append` and `stats` events straight into this cache,
 * so a row is usually fresher than a refetch would make it. Refetching is the
 * fallback for the cases push cannot cover — the tab was asleep, the stream
 * dropped, the operator changed a filter — not the mechanism by which data
 * stays current. A low `staleTime` here would mean 600 rows re-fetched on
 * every focus/mount for data the stream already delivered, so the number is
 * high on purpose and the freshness guarantee comes from the SSE merge plus
 * the focus/reconnect refetches below.
 */
/**
 * A 401 from any query means the session ended — expired, or signed out in
 * another tab. Without this the console would sit there rendering empty tables
 * and retry-able errors, giving the operator no path back to a sign-in form.
 *
 * Flipping the auth entry is enough: `AppGate` reads it and swaps the router
 * for the login screen. Handled at the cache level so EVERY query is covered,
 * including ones added later that nobody thought to guard.
 */
const queryCache = new QueryCache({
  onError: (error, query) => {
    if (!(error instanceof ApiError) || error.status !== 401) return
    // Ignore a 401 from the auth probe itself, or this recurses.
    if (query.queryKey[0] === 'auth') return
    // The deployment's features survive the session ending — see `useFeatures`.
    queryClient.setQueryData<AuthMeResponse>(['auth', 'me'], (prev) => ({
      authenticated: false,
      subject: null,
      features: prev?.features ?? NO_FEATURES,
    }))
  },
})

export const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Keep an unmounted view's data around long enough that navigating
      // away and back is instant, without pinning old pages forever.
      gcTime: 5 * 60_000,

      // The two moments push cannot be trusted: the tab was backgrounded
      // (EventSource may have been throttled or killed) and the network came
      // back (we missed everything sent while offline).
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,

      retry: (failureCount, error) => {
        // 4xx is a bad request, not a blip — replaying it changes nothing.
        // `status === 0` is our transport-failure marker and *is* worth
        // retrying: it usually means the API is mid-restart.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
        return failureCount < 2
      },
      // failureCount is 0 on the first retry decision -> 1s, then 2s.
      retryDelay: (failureCount) => Math.min(30_000, 1_000 * 2 ** failureCount),

      // Keeps row object identity stable across refetches when the bytes did
      // not change, so memoised table rows do not re-render for nothing.
      structuralSharing: true,
    },
    mutations: {
      retry: false,
    },
  },
})
